import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  documentId
} from 'firebase/firestore';
import { getFunctions, httpsCallable /*, connectFunctionsEmulator*/ } from 'firebase/functions';
import { db } from '../firebase';

export default function AdminBroadcast() {
  const navigate = useNavigate();
  const auth = getAuth();

  // Form
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [dataJson, setDataJson] = useState('{\n  "type": "promo"\n}');
  const [saveToFeed, setSaveToFeed] = useState(true);

  // Audience inputs
  const [singleUid, setSingleUid] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [csvFile, setCsvFile] = useState(null);

  // Presets
  const [usePresets, setUsePresets] = useState(false);
  const [presetVerified, setPresetVerified] = useState(false);
  const [presetUnverified, setPresetUnverified] = useState(false);
  const [presetAdmins, setPresetAdmins] = useState(false);
  const [presetHasStripe, setPresetHasStripe] = useState(false);
  const [presetSuspended, setPresetSuspended] = useState(false);
  const [presetActive30d, setPresetActive30d] = useState(false);

  // NEW: Send to everyone
  const [sendAll, setSendAll] = useState(false);

  // Resolved recipients
  const [resolvedUserIds, setResolvedUserIds] = useState([]);

  // UI state
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  // Functions init
  const functions = useMemo(() => {
    const f = getFunctions(undefined, 'us-central1');
    // connectFunctionsEmulator(f, '127.0.0.1', 5001);
    return f;
  }, []);

  // helpers
  const dedupe = (arr) => Array.from(new Set(arr.filter(Boolean)));
  const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };
  const parseList = (text) =>
    text.split(/[\n,\s]+/g).map((s) => s.trim()).filter(Boolean);

  function safeParseJson(input) {
    if (!input.trim()) return undefined;
    try {
      const parsed = JSON.parse(input);
      const coerced = {};
      Object.entries(parsed).forEach(([k, v]) => {
        coerced[String(k)] = typeof v === 'string' ? v : JSON.stringify(v);
      });
      return coerced;
    } catch (_) {
      setError('Invalid JSON in "Data" field.');
      return undefined;
    }
  }

  async function parseCsv(file) {
    const text = await file.text();
    const rows = text.split(/\r?\n/).filter(Boolean);
    if (!rows.length) return [];
    const headers = rows[0].split(',').map((h) => h.trim().toLowerCase());
    const data = [];
    for (let i = 1; i < rows.length; i++) {
      const cols = rows[i].split(',');
      const obj = {};
      headers.forEach((h, idx) => (obj[h] = (cols[idx] || '').trim()));
      data.push(obj);
    }
    return data;
  }

  // Email → UID lookup (IN queries chunked)
  async function lookupUidsByEmails(emails) {
    const unique = dedupe(emails.map((e) => e.toLowerCase()));
    const batches = chunk(unique, 30);
    const uids = [];
    for (const b of batches) {
      const snap = await getDocs(query(collection(db, 'users'), where('email', 'in', b)));
      snap.forEach((d) => uids.push(d.id));
    }
    return dedupe(uids);
  }

  // NEW: Fetch all user IDs (paginated by documentId)
  async function fetchAllUserIds(max = 25000, pageSize = 500) {
    const uids = [];
    let q = query(collection(db, 'users'), orderBy(documentId()), limit(pageSize));
    let lastDoc = null;

    while (true) {
      const snap = await getDocs(q);
      if (snap.empty) break;

      uids.push(...snap.docs.map((d) => d.id));

      if (uids.length >= max) break;
      if (snap.size < pageSize) break;

      lastDoc = snap.docs[snap.docs.length - 1];
      q = query(
        collection(db, 'users'),
        orderBy(documentId()),
        startAfter(lastDoc),
        limit(pageSize)
      );
    }
    return dedupe(uids);
  }

  // Preset loaders
  async function loadPresetUids() {
    const promises = [];

    if (presetVerified) {
      promises.push((async () => {
        const snap = await getDocs(query(collection(db, 'users'), where('isVerified', '==', true)));
        return snap.docs.map((d) => d.id);
      })());
    }
    if (presetUnverified) {
      promises.push((async () => {
        const snap = await getDocs(query(collection(db, 'users'), where('isVerified', '==', false)));
        return snap.docs.map((d) => d.id);
      })());
    }
    if (presetAdmins) {
      promises.push((async () => {
        const a = await getDocs(query(collection(db, 'users'), where('isAdmin', '==', true)));
        const m = await getDocs(query(collection(db, 'users'), where('isMaster', '==', true)));
        return dedupe([...a.docs.map((d) => d.id), ...m.docs.map((d) => d.id)]);
      })());
    }
    if (presetHasStripe) {
      promises.push((async () => {
        const snap = await getDocs(query(collection(db, 'users'), where('stripeAccountId', '!=', null)));
        return snap.docs.map((d) => d.id);
      })());
    }
    if (presetSuspended) {
      promises.push((async () => {
        const snap = await getDocs(collection(db, 'users'));
        const now = Date.now();
        return snap.docs
          .filter((d) => {
            const v = d.data().suspendedUntil;
            const t = typeof v === 'string' ? Date.parse(v) : (v?.toMillis?.() ?? 0);
            return t > now;
          })
          .map((d) => d.id);
      })());
    }
    if (presetActive30d) {
      promises.push((async () => {
        const snap = await getDocs(collection(db, 'users'));
        const cutoff = Date.now() - 30 * 86400000;
        return snap.docs
          .filter((d) => {
            const v = d.data().lastActive;
            const t = typeof v === 'string' ? Date.parse(v) : (v?.toMillis?.() ?? 0);
            return t >= cutoff;
          })
          .map((d) => d.id);
      })());
    }

    const parts = await Promise.all(promises);
    return dedupe(parts.flat());
  }

  // Resolve audience
  const resolveRecipients = async () => {
    setError('');
    setStatus('');
    setResolving(true);
    try {
      let uids = [];

      if (sendAll) {
        uids = await fetchAllUserIds();
      } else {
        if (singleUid.trim()) uids.push(singleUid.trim());

        if (bulkText.trim()) {
          const tokens = parseList(bulkText);
          const emails = tokens.filter((t) => t.includes('@'));
          const ids = tokens.filter((t) => !t.includes('@'));
          uids.push(...ids);
          if (emails.length) {
            const looked = await lookupUidsByEmails(emails);
            uids.push(...looked);
          }
        }

        if (csvFile) {
          const rows = await parseCsv(csvFile);
          const fromUid = rows.map((r) => r.uid).filter(Boolean);
          const fromEmail = rows.map((r) => r.email).filter(Boolean);
          uids.push(...fromUid);
          if (fromEmail.length) {
            const looked = await lookupUidsByEmails(fromEmail);
            uids.push(...looked);
          }
        }

        if (usePresets) {
          const presetUids = await loadPresetUids();
          uids.push(...presetUids);
        }
      }

      uids = dedupe(uids);
      setResolvedUserIds(uids);
      setStatus(`Audience resolved: ${uids.length} recipient${uids.length === 1 ? '' : 's'}.`);
    } catch (e) {
      console.error(e);
      setError('Failed to resolve audience.');
    } finally {
      setResolving(false);
    }
  };

  // Send to me (test)
  const handleSendToMe = async () => {
    const me = auth.currentUser?.uid;
    if (!me) return setError('No signed-in admin user found.');
    await sendPayload([me]);
  };

  // Broadcast
  const handleSend = async () => {
    setError('');
    if (!title.trim() || !body.trim()) {
      setError('Title and Body are required.');
      return;
    }

    if (!resolvedUserIds.length) {
      await resolveRecipients();
    }
    if (!resolvedUserIds.length) {
      setError('No recipients selected.');
      return;
    }

    if (sendAll) {
      const ok = window.confirm(`This will send to ${resolvedUserIds.length} user(s). Continue?`);
      if (!ok) return;
    }

    await sendPayload(resolvedUserIds);
  };

  // Core sender (chunks → callable)
  async function sendPayload(userIds) {
    setSending(true);
    setStatus('Sending…');

    try {
      const payloadData = safeParseJson(dataJson);
      if (dataJson.trim() && !payloadData) {
        setSending(false);
        return;
      }

      const callable = httpsCallable(functions, 'pushNotification');
      const batches = chunk(userIds, 500);

      let sent = 0;
      for (const ids of batches) {
        const payload =
          ids.length === 1
            ? {
                userId: ids[0],
                title: title.trim(),
                body: body.trim(),
                data: payloadData,
                deepLink: deepLink.trim() || undefined,
                saveToFeed
              }
            : {
                userIds: ids,
                title: title.trim(),
                body: body.trim(),
                data: payloadData,
                deepLink: deepLink.trim() || undefined,
                saveToFeed
              };

        // eslint-disable-next-line no-await-in-loop
        await callable(payload);
        sent += ids.length;
        setStatus(`Sending… ${sent}/${userIds.length}`);
      }

      setStatus(`✅ Sent to ${userIds.length} user${userIds.length === 1 ? '' : 's'}.`);
    } catch (e) {
      console.error('pushNotification error', e);
      setError('Failed to send.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>← Back</button>
      <h2 style={styles.header}>📣 Admin Broadcast</h2>

      <div style={styles.card}>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Title</label>
          <input style={styles.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Weekend Promo" />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Body</label>
          <textarea style={styles.textarea} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Short message for push & feed" />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Deep Link (optional)</label>
          <input style={styles.input} value={deepLink} onChange={(e) => setDeepLink(e.target.value)} placeholder="renterapp://bookings/abc123" />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Data (JSON, optional)</label>
          <textarea style={styles.textareaMono} value={dataJson} onChange={(e) => setDataJson(e.target.value)} />
        </div>
        <div style={{ ...styles.fieldRow, alignItems: 'center' }}>
          <label style={styles.label}>Save to in-app feed</label>
          <input type="checkbox" checked={saveToFeed} onChange={(e) => setSaveToFeed(e.target.checked)} />
        </div>
      </div>

      <h3 style={styles.subheader}>🎯 Audience</h3>
      <div style={styles.card}>
        <div style={{ ...styles.checkboxRow, marginBottom: '0.75rem', padding: '0.5rem', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6 }}>
          <input type="checkbox" checked={sendAll} onChange={(e) => setSendAll(e.target.checked)} />
          <strong>Send to everyone</strong>
          <span style={{ color: '#9a3412', marginLeft: 8 }}>(fetches all users, sends in chunks)</span>
        </div>
      </div>

      {!sendAll && (
        <div style={styles.grid}>
          <div style={styles.card}>
            <h4 style={styles.cardTitle}>Direct inputs</h4>
            <div style={styles.fieldRow}>
              <label style={styles.label}>Single UID</label>
              <input style={styles.input} value={singleUid} onChange={(e) => setSingleUid(e.target.value)} placeholder="uid_123" />
            </div>
            <div style={styles.fieldRow}>
              <label style={styles.label}>Bulk UIDs / Emails</label>
              <textarea style={styles.textarea} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder="Paste UIDs or emails (comma / space / line separated)" />
            </div>
            <div style={styles.fieldRow}>
              <label style={styles.label}>CSV (uid,email)</label>
              <input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          <div style={styles.card}>
            <h4 style={styles.cardTitle}>Presets</h4>
            <div style={styles.checkboxRow}><input type="checkbox" checked={usePresets} onChange={(e) => setUsePresets(e.target.checked)} /><span>Enable presets</span></div>
            <div style={styles.presetGrid}>
              <label style={styles.check}><input type="checkbox" disabled={!usePresets} checked={presetVerified} onChange={(e) => setPresetVerified(e.target.checked)} /> Verified</label>
              <label style={styles.check}><input type="checkbox" disabled={!usePresets} checked={presetUnverified} onChange={(e) => setPresetUnverified(e.target.checked)} /> Unverified</label>
              <label style={styles.check}><input type="checkbox" disabled={!usePresets} checked={presetAdmins} onChange={(e) => setPresetAdmins(e.target.checked)} /> Admins</label>
              <label style={styles.check}><input type="checkbox" disabled={!usePresets} checked={presetHasStripe} onChange={(e) => setPresetHasStripe(e.target.checked)} /> Has Stripe account</label>
              <label style={styles.check}><input type="checkbox" disabled={!usePresets} checked={presetSuspended} onChange={(e) => setPresetSuspended(e.target.checked)} /> Suspended</label>
              <label style={styles.check}><input type="checkbox" disabled={!usePresets} checked={presetActive30d} onChange={(e) => setPresetActive30d(e.target.checked)} /> Active in last 30d</label>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
        <button onClick={resolveRecipients} style={styles.secondaryBtn} disabled={resolving}>
          ↻ {sendAll ? 'Resolve everyone' : 'Resolve audience'}
        </button>
        <button onClick={handleSend} style={styles.primaryBtn} disabled={sending || resolving}>Send</button>
        <button onClick={handleSendToMe} style={styles.testBtn} disabled={sending}>Send to me (test)</button>
      </div>

      {!!status && <div style={styles.statusOk}>{status}</div>}
      {!!error && <div style={styles.statusErr}>❌ {error}</div>}

      <div style={styles.hint}>
        <p><strong>Notes</strong></p>
        <ul>
          <li>Calls <code>httpsCallable(functions, 'pushNotification')</code> in chunks of 500 users.</li>
          <li>For very large installs, consider subscribing all tokens to an FCM topic (e.g. <code>all</code>) and send server-side.</li>
        </ul>
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    background: '#f9f9f9',
    minHeight: '100vh'
  },
  backButton: {
    marginBottom: '1.5rem',
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 500
  },
  header: {
    fontSize: '1.5rem',
    margin: '0 0 1rem',
    color: '#065f46'
  },
  subheader: {
    marginTop: '1.25rem',
    marginBottom: '0.5rem',
    color: '#065f46'
  },
  card: {
    background: '#fff',
    borderRadius: 8,
    padding: '1rem',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)',
    marginBottom: '1rem'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem'
  },
  cardTitle: { marginTop: 0, marginBottom: '0.75rem' },
  fieldRow: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.8rem' },
  label: { fontSize: '0.9rem', color: '#333' },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: 6,
    fontSize: '0.95rem'
  },
  textarea: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: 6,
    minHeight: 86,
    fontSize: '0.95rem'
  },
  textareaMono: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: 6,
    minHeight: 120,
    fontSize: '0.9rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
  },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  presetGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.35rem 0.75rem', marginBottom: '0.75rem' },
  check: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  secondaryBtn: {
    background: '#eef2ff',
    border: '1px solid #c7d2fe',
    padding: '0.45rem 0.8rem',
    borderRadius: 6,
    cursor: 'pointer'
  },
  primaryBtn: {
    background: '#065f46',
    color: '#fff',
    border: '1px solid #065f46',
    padding: '0.6rem 1rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600
  },
  testBtn: {
    background: '#fff',
    color: '#065f46',
    border: '1px solid #065f46',
    padding: '0.6rem 1rem',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 600
  },
  statusOk: {
    marginTop: '1rem',
    background: '#e6fffa',
    border: '1px solid #99f6e4',
    color: '#065f46',
    padding: '0.6rem 0.8rem',
    borderRadius: 6
  },
  statusErr: {
    marginTop: '1rem',
    background: '#ffe6e6',
    border: '1px solid #ffc9c9',
    color: '#b91c1c',
    padding: '0.6rem 0.8rem',
    borderRadius: 6
  },
  hint: { marginTop: '1rem', color: '#666', fontSize: '0.9rem' }
};
